import { useEffect, useState } from 'react';
import moment from 'moment';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import { DataTable } from '../../components/nextgen';
import Modal, { DangerModal } from '../../components/Shell/Modal';

import useAsync from '../../hooks/useAsync';

import {
	getErrorLogs,
	getErrorLogsStats,
	bulkDeleteErrorLogs,
} from '../../api/admin';

const getStatusColor = (code) => {
	if (code >= 500) return 'bg-red-100 text-red-800';
	if (code >= 400) return 'bg-yellow-100 text-yellow-800';
	return 'bg-gray-100 text-gray-800';
};

const ErrorDetailModal = ({ error, open, onClose }) => {
	if (!error) return null;

	return (
		<Modal open={open} onClose={onClose} title="Error Details" size="3xl">
			<div className="space-y-6 mt-4">
				{/* Header Info */}
				<div className="flex items-center gap-4">
					<span
						className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${getStatusColor(error.statusCode)}`}
					>
						{error.statusCode}
					</span>
					<span className="text-gray-700 font-mono text-sm font-medium">
						{error.method}
					</span>
					<span className="text-gray-500 text-sm">
						{moment(error.createdAt).format('MMM D, YYYY h:mm:ss A')}
					</span>
				</div>

				{/* Endpoint */}
				<div>
					<label className="block text-sm font-medium text-gray-500 mb-1">
						Endpoint
					</label>
					<div className="bg-gray-50 rounded-md p-3 font-mono text-sm break-all">
						{error.endpoint}
					</div>
				</div>

				{/* Error Message */}
				{error.errorMessage && (
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							Error Message
						</label>
						<div className="bg-red-50 text-red-700 rounded-md p-3 text-sm">
							{error.errorMessage}
						</div>
					</div>
				)}

				{/* User & Organization */}
				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							User
						</label>
						<div className="bg-gray-50 rounded-md p-3 text-sm">
							{error.user ? (
								<>
									<div className="font-medium">
										{error.user.firstName} {error.user.lastName}
									</div>
									<div className="text-gray-500">{error.user.email}</div>
								</>
							) : (
								<span className="text-gray-400">Anonymous</span>
							)}
						</div>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							Organization
						</label>
						<div className="bg-gray-50 rounded-md p-3 text-sm">
							{error.organization?.name || (
								<span className="text-gray-400">-</span>
							)}
						</div>
					</div>
				</div>

				{/* Page URL (Referer) */}
				{error.refererUrl && (
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							Page URL (where user was)
						</label>
						<div className="bg-blue-50 rounded-md p-3 font-mono text-sm text-blue-700 break-all">
							{error.refererUrl}
						</div>
					</div>
				)}

				{/* IP & User Agent */}
				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							IP Address
						</label>
						<div className="bg-gray-50 rounded-md p-3 font-mono text-sm">
							{error.ipAddress || '-'}
						</div>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							User Agent
						</label>
						<div className="bg-gray-50 rounded-md p-3 text-sm text-gray-600 truncate">
							{error.userAgent || '-'}
						</div>
					</div>
				</div>

				{/* Request Body */}
				{error.requestBody &&
					Object.keys(error.requestBody).length > 0 && (
						<div>
							<label className="block text-sm font-medium text-gray-500 mb-1">
								Request Body
							</label>
							<pre className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs overflow-auto max-h-48">
								{JSON.stringify(error.requestBody, null, 2)}
							</pre>
						</div>
					)}

				{/* Query Params */}
				{error.queryParams && Object.keys(error.queryParams).length > 0 && (
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							Query Parameters
						</label>
						<pre className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs overflow-auto max-h-32">
							{JSON.stringify(error.queryParams, null, 2)}
						</pre>
					</div>
				)}

				{/* Stack Trace */}
				{error.errorStack && (
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							Stack Trace
						</label>
						<pre className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs overflow-auto max-h-64 whitespace-pre-wrap">
							{error.errorStack}
						</pre>
					</div>
				)}
			</div>
		</Modal>
	);
};

const ErrorLogs = () => {
	const [page, setPage] = useState(1);
	const [statusFilter, setStatusFilter] = useState('');
	const [methodFilter, setMethodFilter] = useState('');
	const [selectedError, setSelectedError] = useState(null);
	const [selectedRows, setSelectedRows] = useState([]);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const {
		value: errorData,
		execute: refreshErrorLogs,
		status: loadingStatus,
	} = useAsync(getErrorLogs, { immediate: false });

	const { value: statsData, execute: refreshStats } = useAsync(
		getErrorLogsStats,
		{ immediate: false },
	);

	useEffect(() => {
		const params = { page: page - 1, limit: 25 };
		if (statusFilter) params.statusCode = statusFilter;
		if (methodFilter) params.method = methodFilter;
		refreshErrorLogs(params);
	}, [page, statusFilter, methodFilter]);

	useEffect(() => {
		refreshStats({});
	}, []);

	const isLoading = loadingStatus === 'pending';

	const handleBulkDelete = async () => {
		setIsDeleting(true);
		try {
			await bulkDeleteErrorLogs(selectedRows);
			setSelectedRows([]);
			setShowDeleteConfirm(false);
			// Refresh the data and stats
			const params = { page: page - 1, limit: 25 };
			if (statusFilter) params.statusCode = statusFilter;
			if (methodFilter) params.method = methodFilter;
			refreshErrorLogs(params);
			refreshStats({});
		} catch (err) {
			console.error('Failed to delete error logs:', err);
		} finally {
			setIsDeleting(false);
		}
	};

	const columns = [
		{
			key: 'timestamp',
			header: 'Time',
			width: '160px',
			sortable: false,
			render: (row) => (
				<div>
					<div className="text-gray-900 text-sm">
						{moment(row.createdAt).format('MMM D, YYYY')}
					</div>
					<div className="text-gray-500 text-xs">
						{moment(row.createdAt).format('h:mm:ss A')}
					</div>
				</div>
			),
		},
		{
			key: 'status',
			header: 'Status',
			width: '80px',
			sortable: false,
			render: (row) => (
				<span
					className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(row.statusCode)}`}
				>
					{row.statusCode}
				</span>
			),
		},
		{
			key: 'method',
			header: 'Method',
			width: '80px',
			sortable: false,
			render: (row) => (
				<span className="text-gray-700 font-mono text-xs font-medium">
					{row.method}
				</span>
			),
		},
		{
			key: 'endpoint',
			header: 'Endpoint',
			sortable: false,
			render: (row) => (
				<div className="max-w-md">
					<div
						className="text-gray-900 text-sm font-mono truncate"
						title={row.endpoint}
					>
						{row.endpoint}
					</div>
					{row.errorMessage && (
						<div
							className="text-red-600 text-xs truncate mt-0.5"
							title={row.errorMessage}
						>
							{row.errorMessage}
						</div>
					)}
				</div>
			),
		},
		{
			key: 'user',
			header: 'User',
			width: '180px',
			sortable: false,
			render: (row) => (
				<div>
					{row.user ? (
						<>
							<div className="text-gray-900 text-sm">
								{row.user.firstName} {row.user.lastName}
							</div>
							<div className="text-gray-500 text-xs">{row.user.email}</div>
						</>
					) : (
						<span className="text-gray-400 text-sm">Anonymous</span>
					)}
				</div>
			),
		},
		{
			key: 'organization',
			header: 'Organization',
			width: '160px',
			sortable: false,
			render: (row) => (
				<div className="text-gray-700 text-sm">
					{row.organization?.name || '-'}
				</div>
			),
		},
		{
			key: 'ip',
			header: 'IP Address',
			width: '120px',
			sortable: false,
			render: (row) => (
				<span className="text-gray-500 font-mono text-xs">
					{row.ipAddress || '-'}
				</span>
			),
		},
	];

	return (
		<MainContent fullWidth maxWidth="w-full">
			<div className="flex items-center justify-between mb-4">
				<PageHeading divider={false}>Error Tracking</PageHeading>
			</div>

			{/* Stats Cards */}
			{statsData && (
				<div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
					<div className="bg-white border border-gray-200 rounded-lg p-4">
						<div className="text-sm text-gray-500">Total Errors</div>
						<div className="text-2xl font-semibold text-gray-900">
							{statsData.totalCount || 0}
						</div>
					</div>
					{statsData.byStatusCode?.slice(0, 3).map((stat) => (
						<div
							key={stat.statusCode}
							className="bg-white border border-gray-200 rounded-lg p-4"
						>
							<div className="text-sm text-gray-500">
								{stat.statusCode} Errors
							</div>
							<div className="text-2xl font-semibold text-gray-900">
								{stat.count}
							</div>
						</div>
					))}
				</div>
			)}

			{/* Filters */}
			<div className="flex gap-4 mb-4 items-center">
				<select
					value={statusFilter}
					onChange={(e) => {
						setStatusFilter(e.target.value);
						setPage(1);
					}}
					className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
				>
					<option value="">All Status Codes</option>
					<option value="400">400 - Bad Request</option>
					<option value="401">401 - Unauthorized</option>
					<option value="403">403 - Forbidden</option>
					<option value="404">404 - Not Found</option>
					<option value="500">500 - Server Error</option>
				</select>

				<select
					value={methodFilter}
					onChange={(e) => {
						setMethodFilter(e.target.value);
						setPage(1);
					}}
					className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
				>
					<option value="">All Methods</option>
					<option value="GET">GET</option>
					<option value="POST">POST</option>
					<option value="PUT">PUT</option>
					<option value="DELETE">DELETE</option>
					<option value="PATCH">PATCH</option>
				</select>

				{selectedRows.length > 0 && (
					<button
						onClick={() => setShowDeleteConfirm(true)}
						className="ml-auto inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
					>
						Delete {selectedRows.length} selected
					</button>
				)}
			</div>

			<div className="border border-gray-200 rounded-lg overflow-hidden">
				<DataTable
					data={errorData?.rows || []}
					columns={columns}
					loading={isLoading}
					pagination
					serverSidePagination
					currentPage={page}
					totalItems={errorData?.count || 0}
					onPageChange={setPage}
					pageSize={25}
					searchable
					searchPlaceholder="Search errors by endpoint or message..."
					searchFields={['endpoint', 'errorMessage']}
					emptyTitle="No errors found"
					emptyDescription="No API errors have been logged yet"
					hoverable
					bordered={false}
					onRowClick={(row) => setSelectedError(row)}
					selectable
					selectedRows={selectedRows}
					onSelectionChange={setSelectedRows}
				/>
			</div>

			{/* Error Detail Modal */}
			<ErrorDetailModal
				error={selectedError}
				open={!!selectedError}
				onClose={() => setSelectedError(null)}
			/>

			{/* Delete Confirmation Modal */}
			<DangerModal
				open={showDeleteConfirm}
				onClose={() => setShowDeleteConfirm(false)}
				title="Delete Error Logs"
				confirmText={isDeleting ? 'Deleting...' : 'Delete'}
				onConfirm={handleBulkDelete}
			>
				Are you sure you want to delete {selectedRows.length} error log
				{selectedRows.length !== 1 ? 's' : ''}? This action cannot be undone.
			</DangerModal>
		</MainContent>
	);
};

export default ErrorLogs;
